import { common } from '@qwyk/models';
import { TableColumn, CustomFieldDefinitionConverter } from '@qwyk/core';

/*
Columns without nesting, they simply must have the same value for the id and key properties.
Column with a single level of nesting, the key and sub_key properties must be used, each with its corresponding value.
Columns with 2 levels of nesting, as they must be treated specifically in the template, a simple id and key must be used (without "." in the name). Same value for both props. In this case, sub_key is not used.
*/

export function getShipmentColumns(
    customFields: common.CustomFieldDefinition[] = []
): TableColumn[] {
    const uom: string = localStorage.getItem('uom') ?? 'metric';
    const defaultColumns = [
        {
            id: 'name',
            key: 'name',
            label: 'portal.shipment.table.columns.your-reference',
            default: true,
            disabled: true,
            filter: {
                field: 'name',
                type: 'text',
            },
        },
        {
            id: 'organization_shipment_reference',
            key: 'organization_shipment_reference',
            label: 'portal.shipment.table.columns.our-reference',
            default: true,
            disabled: true,
            filter: {
                field: 'organization_shipment_reference',
                type: 'text',
            },
        },
        {
            id: 'transport_mode_product',
            key: 'transport_mode_product',
            label: 'portal.shipment.table.columns.product',
            sort_key: 'transport_mode_product',
            default: false,
            filter: {
                field: 'transport_mode_product',
                type: 'text',
            },
        },
        {
            id: 'tags',
            key: 'tags',
            label: 'common.tags',
            sort_key: 'tags',
            default: false,
            filter: {
                field: 'tags',
                type: 'text',
            },
        },
        {
            id: 'created_on',
            key: 'created_on',
            label: 'portal.shipment.table.columns.date',
            default: false,
            type: 'Date',
            filter: {
                field: 'created_on',
                type: 'date',
            },
        },
        {
            id: 'carrier_name',
            key: 'carrier_name',
            label: 'portal.shipment.table.columns.carrier',
            default: false,
            filter: {
                field: 'carrier_name',
                type: 'text',
            },
        },
        {
            id: 'intermediate_consignee_name',
            key: 'intermediate_consignee_name',
            label: 'portal.shipment.table.columns.intermediate-consignee',
            default: false,
            filter: {
                field: 'intermediate_consignee_name',
                type: 'text',
            },
        },
        {
            id: 'executed_place',
            key: 'executed_place',
            label: 'portal.shipment.table.columns.executed-place',
            default: false,
            filter: {
                field: 'executed_place',
                type: 'text',
            },
        },
        {
            id: 'booking_number',
            key: 'booking_number',
            label: 'portal.shipment.table.columns.booking-number',
            default: false,
            filter: {
                field: 'booking_number',
                type: 'text',
            },
        },
        {
            id: 'issued_by_name',
            key: 'issued_by_name',
            label: 'portal.shipment.table.columns.forwarding-agent',
            default: false,
            filter: {
                field: 'issued_by_name',
                type: 'text',
            },
        },
        {
            id: 'destination_agent_name',
            key: 'destination_agent_name',
            label: 'portal.shipment.table.columns.destination-agent',
            default: false,
            filter: {
                field: 'destination_agent_name',
                type: 'text',
            },
        },
        {
            id: 'direction_text',
            key: 'direction_text',
            label: 'portal.shipment.table.columns.direction',
            default: false,
            filter: {
                field: 'direction_text',
                type: 'text',
            },
        },
        {
            id: 'notes',
            key: 'notes',
            label: 'portal.shipment.table.columns.notes',
            default: false,
            filter: {
                field: 'notes',
                type: 'text',
            },
        },
        {
            id: 'delivery_date',
            key: 'delivery_date',
            label: 'portal.shipment.table.columns.delivery-date',
            default: false,
            type: 'Date',
            filter: {
                field: 'delivery_date',
                type: 'date',
            },
        },
        {
            id: 'received_by_name',
            key: 'received_by_name',
            label: 'portal.shipment.table.columns.received-by',
            default: false,
            filter: {
                field: 'received_by_name',
                type: 'text',
            },
        },
        {
            id: 'pod_comments',
            key: 'pod_comments',
            label: 'portal.shipment.table.columns.pod-comments',
            default: false,
            filter: {
                field: 'pod_comments',
                type: 'text',
            },
        },
        {
            id: 'billing_client_name',
            key: 'billing_client_name',
            label: 'portal.shipment.table.columns.billing-client',
            default: false,
            filter: {
                field: 'billing_client_name',
                type: 'text',
            },
        },
        {
            id: 'entry_number',
            key: 'entry_number',
            label: 'portal.shipment.table.columns.entry-number',
            default: false,
            filter: {
                field: 'entry_number',
                type: 'text',
            },
        },
        {
            id: 'cut_off_date',
            key: 'cut_off_date',
            label: 'portal.shipment.table.columns.cut-off-date',
            default: false,
            type: 'Date',
            filter: {
                field: 'cut_off_date',
                type: 'date',
            },
        },
        {
            id: 'spotting_date',
            key: 'spotting_date',
            label: 'portal.shipment.table.columns.spotting-date',
            default: false,
            type: 'Date',
            filter: {
                field: 'spotting_date',
                type: 'date',
            },
        },
        {
            id: 'insurance_number',
            key: 'insurance_number',
            label: 'portal.shipment.table.columns.insured-by-magaya',
            default: false,
            filter: {
                field: 'insurance_number',
                type: 'text',
            },
        },
        {
            id: 'insurance_date',
            key: 'insurance_date',
            label: 'portal.shipment.table.columns.insurance-date',
            default: false,
            type: 'Date',
            filter: {
                field: 'insurance_date',
                type: 'date',
            },
        },
        {
            id: 'declared_value',
            key: 'declared_value',
            label: 'portal.shipment.table.columns.declared-value',
            default: false,
            filter: {
                field: 'declared_value',
                type: 'numeric',
            },
        },
        {
            id: 'declared_value_for_customs',
            key: 'declared_value_for_customs',
            label: 'portal.shipment.table.columns.declared-value-for-customs',
            default: false,
            filter: {
                field: 'declared_value_for_customs',
                type: 'numeric',
            },
        },
        {
            id: 'has_attachments',
            key: 'has_attachments',
            label: 'portal.shipment.table.columns.attachments',
            default: false,
            filter: {
                field: 'has_attachments',
                type: 'boolean',
            },
        },
        {
            id: 'data.mbl_number',
            key: 'data.mbl_number',
            sub_key: 'mbl_number',
            label: 'portal.shipment.table.columns.master-bl',
            sort_key: 'data.mbl_number',
            default: false,
            filter: {
                field: 'data.mbl_number',
                type: 'text',
            },
        },
        {
            id: 'data.hbl_number',
            key: 'data.hbl_number',
            sub_key: 'hbl_number',
            label: 'portal.shipment.table.columns.house-bl',
            sort_key: 'data.hbl_number',
            default: false,
            filter: {
                field: 'data.hbl_number',
                type: 'text',
            },
        },
        {
            id: 'data.place_of_receipt',
            key: 'data.place_of_receipt',
            sub_key: 'place_of_receipt',
            label: 'portal.shipment.table.columns.origin',
            default: true,
            sort_key: 'data.place_of_receipt.name',
            filter: {
                field: 'data.place_of_receipt.name',
                type: 'text',
            },
        },
        {
            id: 'data.port_of_loading',
            key: 'data.port_of_loading',
            sub_key: 'port_of_loading',
            label: 'portal.shipment.table.columns.pol',
            default: true,
            sort_key: 'data.port_of_loading.name',
            filter: {
                field: 'data.port_of_loading.name',
                type: 'text',
            },
        },
        {
            id: 'data.etd_local',
            key: 'data.etd_local',
            sub_key: 'etd_local',
            label: 'portal.shipment.table.columns.etd',
            default: false,
            type: 'Date',
            sort_key: 'data.etd_local',
            filter: {
                field: 'data.etd_local',
                type: 'date',
            },
        },
        {
            id: 'data.port_of_discharge',
            key: 'data.port_of_discharge',
            sub_key: 'port_of_discharge',
            label: 'portal.shipment.table.columns.pod',
            default: true,
            sort_key: 'data.port_of_discharge.name',
            filter: {
                field: 'data.port_of_discharge.name',
                type: 'text',
            },
        },
        {
            id: 'data.eta_local',
            key: 'data.eta_local',
            sub_key: 'eta_local',
            label: 'portal.shipment.table.columns.eta',
            default: false,
            type: 'Date',
            sort_key: 'data.eta_local',
            filter: {
                field: 'data.eta_local',
                type: 'date',
            },
        },
        {
            id: 'data.place_of_delivery',
            key: 'data.place_of_delivery',
            sub_key: 'place_of_delivery',
            label: 'portal.shipment.table.columns.destination',
            default: true,
            sort_key: 'data.place_of_delivery.name',
            filter: {
                field: 'data.place_of_delivery.name',
                type: 'text',
            },
        },
        {
            id: 'data.shipper_name',
            key: 'data.shipper_name',
            sub_key: 'shipper_name',
            label: 'portal.shipment.table.columns.shipper',
            default: true,
            sort_key: 'data.shipper_name',
            filter: {
                field: 'data.shipper_name',
                type: 'text',
            },
        },
        {
            id: 'data.consignee_name',
            key: 'data.consignee_name',
            sub_key: 'consignee_name',
            label: 'portal.shipment.table.columns.consignee',
            default: true,
            filter: {
                field: 'data.consignee_name',
                type: 'text',
            },
        },
        {
            id: 'data.total_pieces',
            key: 'data.total_pieces',
            sub_key: 'total_pieces',
            label: 'portal.shipment.table.columns.pieces',
            default: true,
            filter: {
                field: 'data.total_pieces',
                type: 'numeric',
            },
        },
        {
            id: 'data.notify_name',
            key: 'data.notify_name',
            sub_key: 'notify_name',
            label: 'portal.shipment.table.columns.notify-party',
            default: false,
            sort_key: 'data.notify_name',
            filter: {
                field: 'data.notify_name',
                type: 'text',
            },
        },
        {
            id: 'data.created_by_name',
            key: 'data.created_by_name',
            sub_key: 'created_by_name',
            label: 'portal.shipment.table.columns.executed-by',
            default: false,
            filter: {
                field: 'data.created_by_name',
                type: 'text',
            },
        },
        {
            id: 'data.total_weight',
            key: 'data.total_weight',
            sub_key: 'total_weight',
            label: 'portal.shipment.table.columns.total-weight',
            default: true,
            sort_key: `data.total_weight.${uom}.value`,
            format: '1.2-2',
            filter: {
                field: `data.total_weight.${uom}.value`,
                type: 'numeric',
            },
        },
        {
            id: 'data.total_volume_weight',
            key: 'data.total_volume_weight',
            sub_key: 'total_volume_weight',
            label: 'portal.shipment.table.columns.volume-weight',
            default: false,
            sort_key: `data.total_volume_weight.${uom}.value`,
            format: '1.3-3',
            filter: {
                field: `data.total_volume_weight.${uom}.value`,
                type: 'numeric',
            },
        },
        {
            id: 'data.total_volume',
            key: 'data.total_volume',
            sub_key: 'total_volume',
            label: 'portal.shipment.table.columns.volume',
            default: true,
            sort_key: `data.total_volume.${uom}.value`,
            format: '1.3-3',
            filter: {
                field: `data.total_volume.${uom}.value`,
                type: 'numeric',
            },
        },
        {
            id: 'data.description',
            key: 'data.description',
            sub_key: 'description',
            label: 'portal.shipment.table.columns.description',
            default: false,
            filter: {
                field: 'data.description',
                type: 'text',
            },
        },
        {
            id: 'data.vessel',
            key: 'data.vessel',
            sub_key: 'vessel',
            label: 'portal.shipment.table.columns.vessel-flight',
            default: false,
            sort_key: 'data.vessel',
            filter: {
                field: 'data.vessel',
                type: 'text',
            },
        },
        {
            id: 'data.voyage',
            key: 'data.voyage',
            sub_key: 'voyage',
            label: 'portal.shipment.table.columns.voyage',
            default: false,
            filter: {
                field: 'data.voyage',
                type: 'text',
            },
        },
        {
            id: 'data.point_of_origin_ftz_number',
            key: 'data.point_of_origin_ftz_number',
            sub_key: 'point_of_origin_ftz_number',
            label: 'portal.shipment.table.columns.point-of-origin',
            default: false,
            filter: {
                field: 'data.point_of_origin_ftz_number',
                type: 'text',
            },
        },
        {
            id: 'data.loading_pier_or_terminal',
            key: 'data.loading_pier_or_terminal',
            sub_key: 'loading_pier_or_terminal',
            label: 'portal.shipment.table.columns.loading-pier',
            default: false,
            filter: {
                field: 'data.loading_pier_or_terminal',
                type: 'text',
            },
        },
        {
            id: 'data.pre_carriage_by',
            key: 'data.pre_carriage_by',
            sub_key: 'pre_carriage_by',
            label: 'portal.shipment.table.columns.pre-carriage-by',
            default: false,
            filter: {
                field: 'data.pre_carriage_by',
                type: 'text',
            },
        },
        {
            id: 'data.place_of_receipt_by_pre_carrier',
            key: 'data.place_of_receipt_by_pre_carrier',
            sub_key: 'place_of_receipt_by_pre_carrier',
            label: 'portal.shipment.table.columns.place-of-receipt-by-pre-carrier',
            default: false,
            filter: {
                field: 'data.place_of_receipt_by_pre_carrier',
                type: 'text',
            },
        },
        {
            id: 'data.external_tracking_numbers',
            key: 'data.external_tracking_numbers',
            sub_key: 'external_tracking_numbers',
            label: 'portal.shipment.table.columns.external-tracking-number',
            default: false,
            filter: {
                field: 'data.external_tracking_numbers',
                type: 'text',
            },
        },
        {
            id: 'data.containers_numbers',
            key: 'data.containers_numbers',
            sub_key: 'containers_numbers',
            label: 'portal.shipment.table.columns.container-numbers',
            default: true,
            filter: {
                field: 'data.containers_numbers',
                type: 'text',
            },
        },
        {
            id: 'data.total_containers',
            key: 'data.total_containers',
            sub_key: 'total_containers',
            label: 'portal.shipment.table.columns.total-containers',
            default: false,
            filter: {
                field: 'data.total_containers',
                type: 'numeric',
            },
        },
        {
            id: 'data.us_customs_origin',
            key: 'data.us_customs_origin',
            sub_key: 'us_customs_origin',
            label: 'portal.shipment.table.columns.origin-port-code',
            default: false,
            sort_key: 'data.us_customs_origin',
            filter: {
                field: 'data.us_customs_origin',
                type: 'text',
            },
        },
        {
            id: 'data.us_customs_destination',
            key: 'data.us_customs_destination',
            sub_key: 'us_customs_destination',
            default: false,
            label: 'portal.shipment.table.columns.destination-port-code',
            sort_key: 'data.us_customs_destination',
            filter: {
                field: 'data.us_customs_destination',
                type: 'text',
            },
        },
        {
            id: 'data.commodities_description',
            key: 'data.commodities_description',
            sub_key: 'commodities_description',
            label: 'portal.shipment.table.columns.commodities-description',
            default: false,
            filter: {
                field: 'data.commodities_description',
                type: 'text',
            },
        },
        {
            id: 'data.purchase_order_numbers',
            key: 'data.purchase_order_numbers',
            sub_key: 'purchase_order_numbers',
            label: 'portal.shipment.table.columns.purchase-order-numbers',
            default: true,
            filter: {
                field: 'data.purchase_order_numbers',
                type: 'text',
            },
        },
        {
            id: 'data.vin_number',
            key: 'data.vin_number',
            sub_key: 'vin_number',
            label: 'portal.shipment.table.columns.vin-numbers',
            default: false,
            filter: {
                field: 'data.vin_number',
                type: 'text',
            },
        },
        {
            id: 'last_event.actual_label',
            key: 'last_event.actual_label',
            label: 'portal.shipment.table.columns.milestone',
            sort_key: 'last_event.actual_label',
            default: false,
            filter: {
                field: 'last_event.actual_label',
                type: 'text',
            },
        },
        {
            id: 'last_event.actual_timestamp',
            key: 'last_event.actual_timestamp',
            label: 'portal.shipment.table.columns.last-date-event',
            default: false,
            type: 'Date',
            filter: {
                field: 'last_event.actual_timestamp',
                type: 'date',
            },
        },
        {
            id: 'last_event.location',
            key: 'last_event.location',
            label: 'portal.shipment.table.columns.last-event-location',
            sort_key: 'last_event.location.label',
            default: false,
            filter: {
                field: 'last_event.location.label',
                type: 'text',
            },
        }
    ];

    const customColumns = customFields.length
        ? CustomFieldDefinitionConverter.toTableColumns(customFields)
        : [];

    return [...defaultColumns, ...customColumns];
}
